/**
 * addit Theme — Global JavaScript
 * Scroll reveals, mobile nav, announcement bar, language switcher
 */

(function () {
  'use strict';

  /* ==========================================================================
     Scroll Reveal (IntersectionObserver)
     ========================================================================== */
  function initScrollReveal() {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      // threshold: 0 — trigger on any intersection. Previously 0.15, but that
      // broke tall sections (e.g. legal pages with a ~5000px richtext block):
      // when the section is taller than the viewport, the max intersection
      // ratio is viewport/section < 0.15, so the observer never fired and the
      // section stayed at opacity: 0 forever. rootMargin still delays the
      // trigger until the section is 60px into the viewport.
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );

    reveals.forEach((el) => observer.observe(el));
  }

  /* ==========================================================================
     Sticky Header scroll state
     ========================================================================== */
  function initStickyHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    window.addEventListener('scroll', () => {
      if (window.scrollY > 50) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }, { passive: true });
  }

  /* ==========================================================================
     Mobile Navigation Drawer
     ========================================================================== */
  function initMobileNav() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const drawer = document.querySelector('[data-menu-drawer]');
    const close = document.querySelector('[data-menu-close]');
    if (!toggle || !drawer) return;

    // Move drawer to <body> so it escapes the section-header stacking context
    // (position:sticky + z-index on .section-header creates a stacking context
    // at z-index:100; the overlay appended to body has z-index:199 and would
    // otherwise sit above the drawer, swallowing all taps on nav links)
    document.body.appendChild(drawer);

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'header__drawer-overlay';
    document.body.appendChild(overlay);

    function openDrawer() {
      drawer.classList.add('is-open');
      overlay.classList.add('is-visible');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', openDrawer);
    if (close) close.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  /* ==========================================================================
     Announcement Bar Dismiss
     ========================================================================== */
  function initAnnouncementBar() {
    const bar = document.querySelector('[data-announcement-bar]');
    const dismiss = document.querySelector('[data-announcement-dismiss]');
    if (!bar || !dismiss) return;

    // Check if already dismissed in this session
    if (sessionStorage.getItem('announcement-dismissed')) {
      bar.style.display = 'none';
      return;
    }

    dismiss.addEventListener('click', () => {
      bar.style.display = 'none';
      sessionStorage.setItem('announcement-dismissed', 'true');
    });
  }

  /* ==========================================================================
     Language Switcher Toggle
     ========================================================================== */
  function initLanguageSwitcher() {
    const toggles = document.querySelectorAll('.language-switcher__toggle');

    toggles.forEach((toggle) => {
      const list = toggle.nextElementSibling;
      if (!list) return;

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = !list.hidden;
        list.hidden = isOpen;
        toggle.setAttribute('aria-expanded', !isOpen);
      });
    });

    // Close on outside click
    document.addEventListener('click', () => {
      document.querySelectorAll('.language-switcher__list').forEach((list) => {
        list.hidden = true;
        const toggle = list.previousElementSibling;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* ==========================================================================
     Scroll Expand — JS fallback (fires when CSS Scroll-Driven Animations
     are not supported; adds .is-expanded once the element enters the viewport)
     ========================================================================== */
  function initScrollExpand() {
    // Native CSS Scroll-Driven Animations handle it — nothing to do
    if (CSS.supports('animation-timeline', 'scroll()')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const elements = document.querySelectorAll('[data-scroll-expand]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-expanded');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ==========================================================================
     Initialize
     ========================================================================== */
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initScrollExpand();
    initStickyHeader();
    initMobileNav();
    initAnnouncementBar();
    initLanguageSwitcher();
  });
})();
